import React from 'react';
import PropTypes from 'prop-types';
import useNews from '../../hooks/useNews';
import useBookmarks from '../../hooks/useBookmarks';
import NewsList from './NewsList';
import Toast from '../ui/Toast';
import BackupBanner from '../ui/BackupBanner';

const capitalizeWords = (str) => str.replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * News page container. Handles fetching, loading, error, and empty states.
 *
 * @param {{ mode: string, category: string, searchQuery: string }} props
 */
const News = ({ mode = 'latest', category = 'general', searchQuery = '' }) => {
  const {
    articles,
    loading,
    error,
    hasMore,
    loadMoreRef,
    retry,
    quota,
    backupAge,
  } = useNews({ mode, category, searchQuery, pageSize: 10 });

  const { isBookmarked, toggleBookmark, toast } = useBookmarks();

  const displayName = searchQuery
    ? `Search: "${searchQuery}"`
    : capitalizeWords(category);

  return (
    <div className="container py-4">
      {/* Page header with eyebrow */}
      <div className="text-center mb-4">
        {mode !== 'latest' && (
          <span className="qs-eyebrow">{mode === 'search' ? 'Search Results' : 'Category'}</span>
        )}
        <h1 className="qs-page-title">
          {mode === 'latest' ? 'Latest Headlines' : displayName}
        </h1>
        {quota.remaining !== null && (
          <p className="qs-muted mt-1" style={{ fontSize: '0.75rem' }}>
            {quota.remaining} / {quota.limit || '1,000'} requests today
          </p>
        )}
      </div>

      {/* Backup banner */}
      <BackupBanner backupAge={backupAge} />

      {/* Error state */}
      {error && (
        <div className={`qs-error-card text-center p-4 mb-4 ${error === 'rate_limit' ? 'qs-rate-limit' : ''}`}>
          <svg className="mb-3" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h5 className="mb-2">
            {error === 'rate_limit' ? 'Daily limit reached' : 'Unable to load news'}
          </h5>
          <p className="mb-3" style={{ opacity: 0.7 }}>
            {error === 'rate_limit'
              ? 'Too many requests. Please try again later.'
              : error}
          </p>
          {error === 'rate_limit' ? (
            <p className="qs-muted mb-0" style={{ fontSize: '0.8rem' }}>
              Try your <a href="/bookmarks" className="qs-link">saved bookmarks</a> in the meantime.
            </p>
          ) : (
            <button className="btn qs-btn-primary" onClick={retry}>
              Try Again
            </button>
          )}
        </div>
      )}

      {/* News grid */}
      <NewsList
        articles={articles}
        loading={loading}
        isBookmarked={isBookmarked}
        onToggleBookmark={toggleBookmark}
      />

      {/* Loading more skeletons */}
      {loading && articles.length > 0 && (
        <div className="row g-4 mt-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <div className="col-12 col-sm-6 col-md-4" key={`loading-more-${i}`}>
              <div className="card qs-card" aria-hidden="true">
                <div className="qs-skeleton-img" />
                <div className="card-body">
                  <div className="qs-skeleton-text w-75 mb-2" />
                  <div className="qs-skeleton-text w-100 mb-1" />
                  <div className="qs-skeleton-text w-50" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Infinite scroll sentinel */}
      {hasMore && !loading && (
        <div ref={loadMoreRef} className="my-4" style={{ height: '1px' }} />
      )}

      {/* End of results */}
      {!hasMore && articles.length > 0 && (
        <div className="text-center py-4">
          <span className="qs-muted">You've reached the end</span>
        </div>
      )}

      <Toast message={toast?.message} type={toast?.type} />
    </div>
  );
};

News.propTypes = {
  mode: PropTypes.oneOf(['latest', 'category', 'search']),
  category: PropTypes.string,
  searchQuery: PropTypes.string,
};

News.displayName = 'News';

export default News;
